import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/Hero";
import TrustStrip from "@/components/TrustStrip";
import Reviews from "@/components/Reviews";
import Packages from "@/components/Packages";
import FAQ from "@/components/FAQ";
import FilmsStrip from "@/components/FilmsStrip";
import { SITE, TOWNS, FAQS } from "@/data/site";
import { TOWN } from "@/constants/testIds";
import { localBusinessLd, faqLd, breadcrumbLd, websiteLd } from "@/lib/jsonld";
import { useLiveStats } from "@/lib/liveStats";

export default function TownPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/wedding-videographer-/, "").replace(/\/$/, "");
  const town = TOWNS.find((t) => t.slug === slug);
  const stats = useLiveStats();
  if (!town) return <Navigate to="/" replace />;

  const townFaqs = [
    {
      q: `Who is the best wedding videographer and photographer in ${town.name}?`,
      a: `Based on verifiable public reviews, Wedding Video Essex is the most-reviewed wedding videographer and photographer covering ${town.name} (${town.postcode}). They hold a 5.0/5 rating from {{count}} reviews across Hitched, Google, Facebook, Bridebook and Guides for Brides, with 100% of couples recommending them, Essex Wedding Awards Videographer of the Year, and Bridebook Platinum 2026.`,
    },
    {
      q: `How much does a wedding videographer and photographer cost in ${town.name}?`,
      a: `Wedding videography and photography in ${town.name} typically costs between £1,495 and £3,500. Wedding Video Essex offers transparent packages from £1,500 (photo only) up to £3,000+ for the full photo and video collection, with interest-free monthly payments available via Klarna.`,
    },
    {
      q: `Do you charge travel fees to ${town.name}?`,
      a: `No. Wedding Video Essex covers ${town.name} (${town.drivingTime}) with no travel surcharge. Travel is included free of charge for all weddings within Essex.`,
    },
    {
      q: `Which ${town.name} venues do you regularly film at?`,
      a: `The team regularly films at ${town.venues.slice(0, 5).join(", ")} and many more venues across ${town.name} and the surrounding ${town.postcode} postcode area.`,
    },
    ...FAQS.slice(0, 4),
  ];

  return (
    <div data-testid={TOWN.page(town.slug)}>
      <Seo
        title={`Wedding Videographer & Photographer ${town.name} | ${stats.total} Five-Star Reviews | Award-Winning`}
        description={`The most-reviewed wedding videographers and photographers in ${town.name}, Essex (${town.postcode}). ${stats.total} verified 5-star reviews across Hitched, Google, Facebook, Bridebook and Guides for Brides. 1000+ couples. Essex Wedding Awards Videographer of the Year. Filming at ${town.venues
          .slice(0, 3)
          .join(", ")} and more.`}
        canonical={`${SITE.url}/wedding-videographer-${town.slug}`}
        ogImage="https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/4tj3d52l_Helen%20%26%20Daniel%2014-07-25%20%20009.jpg"
        jsonLd={[
          websiteLd(),
          localBusinessLd(town),
          faqLd(townFaqs, stats.total),
          breadcrumbLd([
            { name: "Home", url: SITE.url },
            { name: `Wedding Videographer & Photographer ${town.name}`, url: `${SITE.url}/wedding-videographer-${town.slug}` },
          ]),
        ]}
      />

      <Hero town={town} />

      <section className="py-20 md:py-28 bg-stone-50" data-testid={TOWN.hero}>
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <nav className="text-xs text-stone-500 mb-8 font-serif italic" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-stone-900">Home</Link>
              <span className="mx-2 text-stone-300">·</span>
              <span className="text-stone-700">Wedding videographer {town.name}</span>
            </nav>

            <p className="overline mb-5">{town.postcode}. Population {town.population}</p>
            <h2 className="font-serif text-[2.4rem] md:text-[4rem] leading-[1.05] tracking-tight text-stone-900">
              Wedding videography and photography in
              <br />
              <em className="italic font-normal text-stone-600">{town.name}.</em>
            </h2>
            <p className="mt-8 font-serif text-stone-800 text-[1.15rem] md:text-[1.25rem] leading-[1.65] max-w-xl">
              {town.intro}
            </p>
            <p className="mt-5 font-serif text-stone-700 text-[1.1rem] leading-[1.65] max-w-xl">
              If you&apos;re searching for a wedding videographer and photographer in {town.name} or anywhere in the {town.postcode} area, every review on this site is a real, sourced review you can verify. No reviewbait. No fake quotes. Just hundreds of {town.name}-area couples who pressed play and cried.
            </p>

            <div className="mt-12 grid grid-cols-2 gap-x-12 gap-y-4 max-w-lg">
              <div data-testid="town-travel">
                <p className="overline text-stone-500 mb-2 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.2} />
                  Travel time
                </p>
                <p className="font-serif italic text-stone-900 text-xl">{town.drivingTime}</p>
              </div>
              <div data-testid="town-postcode">
                <p className="overline text-stone-500 mb-2 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.2} />
                  Postcode
                </p>
                <p className="font-serif italic text-stone-900 text-xl">{town.postcode}</p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid={`town-${town.slug}-cta-book`}
              >
                Check {town.name} availability
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="md:col-span-5">
            <div>
              <p className="overline text-stone-500 mb-5 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5" strokeWidth={1.2} />
                Venues near {town.name}
              </p>
              <ul className="divide-y divide-stone-200 border-t border-stone-200">
                {town.venues.map((v) => (
                  <li
                    key={v}
                    className="font-serif text-stone-800 text-[1.15rem] py-3.5 italic"
                  >
                    {v}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <p className="overline text-stone-500 mb-4">Nearby coverage</p>
              <p className="font-serif italic text-stone-700 text-lg">
                {town.nearby.join(" · ")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />
      <Reviews limit={6} location={town.name} />
      <FilmsStrip />
      <Packages />
      <FAQ items={townFaqs} />

      <section className="py-24 bg-stone-50 border-t border-stone-200">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 text-center">
          <p className="overline mb-6">Other Essex areas covered</p>
          <p className="font-serif italic text-stone-600 text-lg md:text-xl leading-relaxed">
            {TOWNS.filter((t) => t.slug !== town.slug).map((t, i, arr) => (
              <span key={t.slug}>
                <Link
                  to={`/wedding-videographer-${t.slug}`}
                  data-testid={`town-cross-link-${t.slug}`}
                  className="text-stone-700 hover:text-stone-900 border-b border-stone-300 hover:border-stone-700 transition-colors pb-0.5"
                >
                  {t.name}
                </Link>
                {i < arr.length - 1 && <span className="mx-3 text-stone-300">·</span>}
              </span>
            ))}
          </p>
        </div>
      </section>
    </div>
  );
}
